import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slider,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  ArrowForward,
  AutoAwesome,
  Calculate,
  CheckCircle,
  Close,
  Fingerprint,
  FitnessCenter,
  MonitorWeight,
  PhoneAndroid,
  QrCode2,
  Schedule,
  Send,
  Star,
  TrendingUp,
} from "@mui/icons-material";
import DownloadAppModal from "../components/DownloadAppModal";
import GymJourney from "../components/GymJourney";
import FeatureExplorer from "../components/FeatureExplorer";
import { feedbackAPI } from "../services/api";

// ── Light Theme Styling Tokens (Black & Purple) ─────────────────────────────
const INK = "#0F172A";
const MUTED = "#64748B";
const SOFT = "#F8FAFC";
const SURFACE = "#FFFFFF";
const BORDER = "#E2E8F0";
const ACCENT = "#7C3AED"; // Fitistic Primary Purple Accent
const BLUE = "#2563EB";
const INDIGO = "#6366F1";
const GREEN = "#059669";
const PURPLE = "#8B5CF6";
const CYAN = "#0891B2";

const TESTIMONIALS = [
  {
    name: "Vikram Malhotra",
    role: "Owner, Iron Pulse Gym (3 Locations)",
    quote: "Fitistic replaced our scattered spreadsheets and 3 different apps. Lead conversion jumped 32% in our first 60 days!",
    rating: 5,
    avatarColor: BLUE,
  },
  {
    name: "Ananya Sharma",
    role: "Founder, FitNation Studio",
    quote: "The member app and pause membership feature saved us hours of front-desk admin work every week. Members love it!",
    rating: 5,
    avatarColor: INDIGO,
  },
  {
    name: "Rajesh Kumar",
    role: "Operations Head, Titan Fitness",
    quote: "Biometric eSSL sync paired with automatic UPI invoice reminders reduced our fee defaults by over 45%. Outstanding platform.",
    rating: 5,
    avatarColor: GREEN,
  },
];

export default function LandingPage() {
  const { search } = useLocation();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [getStartedOpen, setGetStartedOpen] = useState(() => new URLSearchParams(search).get('demo') === '1');

  // Interactive Live Preview State
  const [activeTab, setActiveTab] = useState(0);

  // ROI Calculator State
  const [memberCount, setMemberCount] = useState(150);
  const [monthlyFee, setMonthlyFee] = useState(2500);

  // Feature Filter State

  // Contact Form State
  const [form, setForm] = useState({
    name: "",
    email: "",
    gymName: "",
    phone: "",
    message: "",
    plan: "Product Demo",
  });
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    window.scrollTo({ left: 0 });
  }, []);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleOnboardingSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    setSubmitting(true);
    const planName = form.plan || 'Product Demo';
    const defaultNote = 'Requested 1-on-1 walkthrough & product demo';

    const finalMessage = form.message?.trim() ? form.message.trim() : defaultNote;

    try {
      await feedbackAPI.submitOnboarding({
        ...form,
        plan: planName,
        message: finalMessage,
      });
      setSnack({ open: true, msg: "Thank you! Our onboarding specialist will reach out within 2 hours.", severity: "success" });
      setForm({ name: "", email: "", gymName: "", phone: "", message: "", plan: "Product Demo" });
      setGetStartedOpen(false);
    } catch {
      setSnack({ open: true, msg: "Something went wrong. Please try again.", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // ROI Calculations
  const currentAnnualRevenue = memberCount * monthlyFee * 12;
  const projectedRetentionGain = currentAnnualRevenue * 0.18; // 18% churn reduction
  const projectedLeadConversionGain = (memberCount * 0.25) * monthlyFee * 12; // 25% new leads converted
  const totalExtraRevenue = projectedRetentionGain + projectedLeadConversionGain;

  const primaryButtonSx = {
    background: `linear-gradient(135deg, ${ACCENT} 0%, #6366F1 100%)`,
    color: "#FFFFFF",
    borderRadius: "12px",
    px: 3.5,
    py: 1.4,
    fontWeight: 800,
    fontSize: "15px",
    textTransform: "none",
    boxShadow: "0 8px 24px rgba(124, 58, 237, 0.35)",
    transition: "all 0.25s ease",
    "&:hover": {
      background: `linear-gradient(135deg, #8B5CF6 0%, #4F46E5 100%)`,
      boxShadow: "0 12px 30px rgba(124, 58, 237, 0.5)",
      transform: "translateY(-2px)",
    },
  };

  const outlineButtonSx = {
    border: `1.5px solid ${BORDER}`,
    color: INK,
    borderRadius: "12px",
    px: 3.5,
    py: 1.4,
    fontWeight: 800,
    fontSize: "15px",
    textTransform: "none",
    bgcolor: SURFACE,
    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
    transition: "all 0.25s ease",
    "&:hover": {
      bgcolor: SOFT,
      borderColor: "#CBD5E1",
      transform: "translateY(-2px)",
    },
  };

  const fieldSx = {
    "& .MuiInputLabel-root": { color: MUTED, fontWeight: 700, fontSize: "14px" },
    "& .MuiOutlinedInput-root": {
      bgcolor: SURFACE,
      color: INK,
      borderRadius: "12px",
      transition: "all 0.2s ease",
      "& fieldset": { borderColor: BORDER, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
      "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(124, 58, 237, 0.12)" },
    },
    "& .MuiOutlinedInput-input": { color: INK, py: 1.3, px: 1.75 },
  };



  return (
    <Box sx={{ bgcolor: SURFACE, minHeight: "100vh", color: INK, width: "100%", overflowX: "clip" }}>

      <PublicHeader onBookDemo={() => {
        setForm(prev => ({ ...prev, plan: 'Product Demo', message: '' }));
        setGetStartedOpen(true);
      }} />

      {/* ── HERO SECTION WITH LIGHT MESH BACKGROUND & LIVE PREVIEW ──────────── */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 14 },
          background: `linear-gradient(180deg, #FFFFFF 0%, ${SOFT} 70%, #FFFFFF 100%)`,
          overflow: "hidden",
        }}
      >
        {/* Soft Ambient Mesh Glows */}
        <Box sx={{ position: "absolute", top: -80, left: "15%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${alpha(ACCENT, 0.08)} 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: 0, right: "10%", width: 550, height: 550, borderRadius: "50%", background: `radial-gradient(circle, ${alpha(BLUE, 0.08)} 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />

        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Hero Left Content */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2, py: 0.75, borderRadius: "30px", bgcolor: alpha(ACCENT, 0.08), border: `1px solid ${alpha(ACCENT, 0.2)}`, color: ACCENT, mb: 3 }}>
                <AutoAwesome sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.02em" }}>
                  The #1 Gym CRM & Growth Operating System
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.2rem" },
                  lineHeight: 1.05,
                  letterSpacing: "-0.03em",
                  mb: 2.5,
                  color: INK,
                }}
              >
                Grow Members.<br />
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${INDIGO} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Maximize Revenue.
                </Box>
              </Typography>

              <Typography sx={{ color: MUTED, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.7, mb: 4.5, maxWidth: 540 }}>
                Fitistic Admin unites lead capture, automated WhatsApp follow-ups, biometric attendance, branded member app, and UPI billing into one high-powered operating platform.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 5 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => {
                    setForm((prev) => ({ ...prev, plan: "Product Demo", message: "" }));
                    setGetStartedOpen(true);
                  }}
                  sx={primaryButtonSx}
                >
                  Start Free Onboarding
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhoneAndroid />}
                  onClick={() => setDownloadOpen(true)}
                  sx={outlineButtonSx}
                >
                  Explore Member App
                </Button>
              </Stack>

              {/* Quick Feature Badges */}
              <Grid container spacing={2}>
                {[
                  { icon: <CheckCircle sx={{ color: GREEN, fontSize: 18 }} />, text: "eSSL Biometric & Smart Scale Sync" },
                  { icon: <CheckCircle sx={{ color: GREEN, fontSize: 18 }} />, text: "Built-in Member Pause Management" },
                  { icon: <CheckCircle sx={{ color: GREEN, fontSize: 18 }} />, text: "Automated UPI QR Billing" },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {item.icon}
                      <Typography sx={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                        {item.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Hero Right Interactive Command Center Preview */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: SURFACE,
                  borderRadius: "24px",
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08), 0 0 30px rgba(124, 58, 237, 0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Window Header Bar */}
                <Box sx={{ px: 3, py: 2, bgcolor: SOFT, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FF5F56" }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FFBD2E" }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#27C93F" }} />
                    <Typography sx={{ ml: 1, fontSize: 12, color: MUTED, fontWeight: 700 }}>
                      fitistic-command-center.app
                    </Typography>
                  </Stack>

                  {/* Tab Selector */}
                  <Stack direction="row" spacing={0.5}>
                    {["Sales CRM", "Member App", "Attendance"].map((label, idx) => (
                      <Chip
                        key={label}
                        label={label}
                        size="small"
                        onClick={() => setActiveTab(idx)}
                        sx={{
                          bgcolor: activeTab === idx ? ACCENT : alpha(BORDER, 0.6),
                          color: activeTab === idx ? "#FFFFFF" : INK,
                          fontWeight: 800,
                          fontSize: 11,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: activeTab === idx ? ACCENT : BORDER },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* Tab 0: Sales Command Center */}
                {activeTab === 0 && (
                  <Box sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, fontSize: 18, color: INK }}>
                          Sales Pipeline & Lead Command
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                          Real-time conversion dashboard
                        </Typography>
                      </Box>
                      <Chip icon={<TrendingUp sx={{ color: `${GREEN} !important` }} />} label="+34% Revenue" sx={{ bgcolor: alpha(GREEN, 0.12), color: GREEN, fontWeight: 900, fontSize: 12 }} />
                    </Stack>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: SOFT, borderRadius: "14px", border: `1px solid ${alpha(BLUE, 0.2)}` }}>
                          <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 700 }}>ACTIVE PIPELINE</Typography>
                          <Typography sx={{ fontSize: 24, fontWeight: 950, color: INK, my: 0.5 }}>₹4.2 Lakh</Typography>
                          <Typography sx={{ fontSize: 11, color: BLUE, fontWeight: 800 }}>38 leads active</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: SOFT, borderRadius: "14px", border: `1px solid ${alpha(GREEN, 0.2)}` }}>
                          <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 700 }}>CONVERSION RATE</Typography>
                          <Typography sx={{ fontSize: 24, fontWeight: 950, color: INK, my: 0.5 }}>28.5%</Typography>
                          <Typography sx={{ fontSize: 11, color: GREEN, fontWeight: 800 }}>+6.2% vs avg</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: SOFT, borderRadius: "14px", border: `1px solid ${alpha(ACCENT, 0.2)}` }}>
                          <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 700 }}>TRIALS DUE</Typography>
                          <Typography sx={{ fontSize: 24, fontWeight: 950, color: INK, my: 0.5 }}>14</Typography>
                          <Typography sx={{ fontSize: 11, color: ACCENT, fontWeight: 800 }}>Follow up today</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Live Lead Cards */}
                    <Stack spacing={1.5}>
                      {[
                        { name: "Aarav Sharma", phone: "+91 98765 43210", stage: "Trial Booked", color: BLUE, plan: "Annual Gold" },
                        { name: "Priya Patel", phone: "+91 98123 45678", stage: "Payment Link Sent", color: GREEN, plan: "Quarterly Pro" },
                        { name: "Rahul Verma", phone: "+91 97890 12345", stage: "Walk-in Inquiry", color: ACCENT, plan: "Monthly Pass" },
                      ].map((lead, i) => (
                        <Paper key={i} elevation={0} sx={{ p: 1.75, bgcolor: SOFT, borderRadius: "12px", border: `1px solid ${BORDER}` }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: `${lead.color}15`, color: lead.color, display: "grid", placeItems: "center", fontWeight: 900 }}>
                                {lead.name[0]}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK }}>{lead.name}</Typography>
                                <Typography sx={{ fontSize: 11, color: MUTED }}>{lead.phone} • {lead.plan}</Typography>
                              </Box>
                            </Stack>
                            <Chip label={lead.stage} size="small" sx={{ bgcolor: `${lead.color}15`, color: lead.color, fontWeight: 800, fontSize: 11 }} />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Tab 1: Branded Member App Preview */}
                {activeTab === 1 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 18, color: INK, mb: 0.5 }}>
                      Branded Member App Experience
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mb: 3 }}>
                      Included for all your gym members
                    </Typography>

                    <Paper elevation={0} sx={{ p: 2.5, bgcolor: SOFT, borderRadius: "18px", border: `1px solid ${alpha(INDIGO, 0.2)}`, maxWidth: 360, mx: "auto", textAlign: "left" }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: INDIGO, color: "#FFF", display: "grid", placeItems: "center", fontWeight: 900 }}>
                          V
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 900, color: INK, fontSize: 15 }}>Vikrant Singh</Typography>
                          <Typography sx={{ fontSize: 12, color: GREEN, fontWeight: 700 }}>● Active Member • Titan Fitness</Typography>
                        </Box>
                      </Stack>

                      {/* Pause Feature Banner in App */}
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(ACCENT, 0.08), borderRadius: "12px", border: `1px solid ${alpha(ACCENT, 0.2)}`, mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Schedule sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK }}>Pause Membership</Typography>
                          </Stack>
                          <Chip label="Configured by Gym Admin" size="small" sx={{ bgcolor: ACCENT, color: "#FFF", fontSize: 9, fontWeight: 900 }} />
                        </Stack>
                      </Paper>

                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, bgcolor: SURFACE, borderRadius: "10px", border: `1px solid ${BORDER}`, textAlign: "center" }}>
                            <QrCode2 sx={{ color: CYAN, fontSize: 24 }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: INK, mt: 0.5 }}>QR Check-in</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, bgcolor: SURFACE, borderRadius: "10px", border: `1px solid ${BORDER}`, textAlign: "center" }}>
                            <FitnessCenter sx={{ color: PURPLE, fontSize: 24 }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: INK, mt: 0.5 }}>Workout Plan</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                )}

                {/* Tab 2: Biometrics & Attendance Preview */}
                {activeTab === 2 && (
                  <Box sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 18, color: INK, mb: 0.5 }}>
                      Biometric eSSL & Smart Attendance
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mb: 3 }}>
                      Live sync across all gym entry turnstiles
                    </Typography>

                    <Stack spacing={2}>
                      {[
                        { title: "eSSL Fingerprint Device #1", status: "ONLINE", membersToday: 142, icon: <Fingerprint sx={{ color: GREEN }} /> },
                        { title: "Front Desk QR Scanner", status: "ONLINE", membersToday: 98, icon: <QrCode2 sx={{ color: BLUE }} /> },
                        { title: "InBody Smart Scale Integration", status: "SYNCED", membersToday: 34, icon: <MonitorWeight sx={{ color: CYAN }} /> },
                      ].map((item, idx) => (
                        <Paper key={idx} elevation={0} sx={{ p: 2, bgcolor: SOFT, borderRadius: "14px", border: `1px solid ${BORDER}` }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box sx={{ p: 1, borderRadius: "10px", bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>{item.icon}</Box>
                              <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK }}>{item.title}</Typography>
                                <Typography sx={{ fontSize: 11, color: MUTED }}>{item.membersToday} check-ins processed today</Typography>
                              </Box>
                            </Stack>
                            <Chip label={item.status} size="small" sx={{ bgcolor: alpha(GREEN, 0.12), color: GREEN, fontWeight: 900, fontSize: 10 }} />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <GymJourney onBookDemo={() => {
        setForm((prev) => ({ ...prev, plan: "Product Demo", message: "" }));
        setGetStartedOpen(true);
      }} />

      {/* ── INTERACTIVE GYM REVENUE & ROI CALCULATOR ────────────────────────── */}
      <Box id="roi-calculator" sx={{ py: { xs: 8, md: 12 }, bgcolor: SURFACE, position: "relative" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 700, mx: "auto", mb: 7 }}>
            <Chip icon={<Calculate sx={{ color: `${ACCENT} !important` }} />} label="Revenue Calculator" sx={{ bgcolor: alpha(ACCENT, 0.08), color: ACCENT, fontWeight: 900, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 950, fontSize: { xs: "2rem", md: "2.8rem" }, letterSpacing: "-0.02em", color: INK, mb: 2 }}>
              Calculate Your Revenue Growth
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 16 }}>
              See how much additional annual revenue Fitistic CRM generates for your gym by stopping member churn and doubling lead conversion.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              bgcolor: SOFT,
              borderRadius: "24px",
              border: `1px solid ${BORDER}`,
              boxShadow: "0 16px 48px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Grid container spacing={6} alignItems="center">
              {/* Sliders Input */}
              <Grid item xs={12} md={6}>
                <Stack spacing={4}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: INK, fontSize: 15 }}>Number of Active Gym Members</Typography>
                      <Typography sx={{ fontWeight: 900, color: ACCENT, fontSize: 18 }}>{memberCount} Members</Typography>
                    </Stack>
                    <Slider
                      value={memberCount}
                      min={30}
                      max={1000}
                      step={10}
                      onChange={(e, val) => setMemberCount(val)}
                      sx={{ color: ACCENT, height: 8 }}
                    />
                  </Box>

                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: INK, fontSize: 15 }}>Average Monthly Membership Fee</Typography>
                      <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: 18 }}>₹{monthlyFee.toLocaleString("en-IN")}</Typography>
                    </Stack>
                    <Slider
                      value={monthlyFee}
                      min={500}
                      max={10000}
                      step={250}
                      onChange={(e, val) => setMonthlyFee(val)}
                      sx={{ color: BLUE, height: 8 }}
                    />
                  </Box>
                </Stack>
              </Grid>

              {/* Calculated Growth Card */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 4, bgcolor: SURFACE, borderRadius: "20px", border: `1px solid ${alpha(GREEN, 0.3)}`, textAlign: "center", boxShadow: "0 8px 30px rgba(16,185,129,0.08)" }}>
                  <Typography sx={{ fontSize: 13, color: MUTED, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Projected Extra Annual Revenue
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 950, color: GREEN, my: 1 }}>
                    +₹{Math.round(totalExtraRevenue).toLocaleString("en-IN")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED, mb: 3 }}>
                    Calculated via +25% trial conversions & -18% member churn rate.
                  </Typography>
                  <Button variant="contained" endIcon={<ArrowForward />} onClick={() => setGetStartedOpen(true)} sx={primaryButtonSx} fullWidth>
                    Claim Your Gym Growth Demo
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      <FeatureExplorer onBookDemo={(feature) => {
        setForm((prev) => ({ ...prev, plan: "Product Demo", message: `I'd like a walkthrough of ${feature.toLowerCase()} for my gym.` }));
        setGetStartedOpen(true);
      }} />

      {/* ── TESTIMONIALS SECTION ────────────────────────────────────────────── */}
      <Box id="testimonials" sx={{ py: { xs: 8, md: 12 }, bgcolor: SURFACE, color: INK }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 700, mx: "auto", mb: 7 }}>
            <Typography variant="h2" sx={{ fontWeight: 950, fontSize: { xs: "2rem", md: "2.8rem" }, color: INK, mb: 2 }}>
              Loved by 500+ Gym Owners
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 16 }}>
              Here is what fitness studio owners say about scaling with Fitistic.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {TESTIMONIALS.map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    bgcolor: SOFT,
                    borderRadius: "20px",
                    border: `1px solid ${BORDER}`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                      {[...Array(item.rating)].map((_, i) => (
                        <Star key={i} sx={{ color: "#F59E0B", fontSize: 20 }} />
                      ))}
                    </Stack>
                    <Typography sx={{ fontSize: 15, lineHeight: 1.7, color: INK, fontStyle: "italic", mb: 3 }}>
                      "{item.quote}"
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 42, height: 42, borderRadius: "50%", bgcolor: item.avatarColor, color: "#FFF", display: "grid", placeItems: "center", fontWeight: 900 }}>
                      {item.name[0]}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: INK, fontSize: 15 }}>{item.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>{item.role}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── FOOTER CTA & CONTACT SECTION ───────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: SOFT, borderTop: `1px solid ${BORDER}` }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, bgcolor: SURFACE, borderRadius: "24px", border: `1px solid ${BORDER}`, boxShadow: "0 16px 48px rgba(15, 23, 42, 0.06)" }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography variant="h3" sx={{ fontWeight: 950, fontSize: { xs: 24, md: 32 }, color: INK, mb: 1 }}>
                Ready to Upgrade Your Gym Operations?
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 15 }}>
                Fill out the form below for a personalized platform demo & setup walkthrough.
              </Typography>
            </Box>

            <ContactForm
              form={form}
              submitting={submitting}
              onChange={handleFormChange}
              onSubmit={handleOnboardingSubmit}
              primaryButton={primaryButtonSx}
              fieldSx={fieldSx}
            />
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 8, pt: 4, borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>
              © {new Date().getFullYear()} Fitistic CRM. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Typography component="a" href="/privacy-policy" sx={{ color: MUTED, fontSize: 13, textDecoration: "none", "&:hover": { color: INK } }}>
                Privacy Policy
              </Typography>
              <Typography component="a" href="/terms" sx={{ color: MUTED, fontSize: 13, textDecoration: "none", "&:hover": { color: INK } }}>
                Terms of Service
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── MODALS ────────────────────────────────────────────────────────── */}
      <Dialog
        open={getStartedOpen}
        onClose={() => setGetStartedOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            bgcolor: SURFACE,
            color: INK,
            boxShadow: "0 25px 70px rgba(15, 23, 42, 0.18)",
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 3, sm: 4 },
            pt: { xs: 3, sm: 3.5 },
            pb: 2,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box>
            <Typography fontWeight={950} fontSize={{ xs: 20, sm: 22 }} color={INK} letterSpacing="-0.02em">
              Book a Product Demo
            </Typography>
            <Typography fontSize={13} color={MUTED} mt={0.3}>
              Schedule a personalized walkthrough tailored to your gym and its needs.
            </Typography>
          </Box>
          <IconButton onClick={() => setGetStartedOpen(false)} sx={{ color: MUTED, p: 1, "&:hover": { bgcolor: SOFT } }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 3, sm: 4 }, pt: 3.5, pb: 4 }}>
          <ContactForm
            compact
            form={form}
            submitting={submitting}
            onChange={handleFormChange}
            onSubmit={handleOnboardingSubmit}
            primaryButton={primaryButtonSx}
            fieldSx={fieldSx}
          />
        </DialogContent>
      </Dialog>

      <DownloadAppModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((c) => ({ ...c, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <MuiAlert onClose={() => setSnack((c) => ({ ...c, open: false }))} severity={snack.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 700 }}>
          {snack.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function ContactForm({ form, submitting, onChange, onSubmit, primaryButton, fieldSx, compact = false }) {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ pt: compact ? 1 : 0 }}>
      <Grid container columnSpacing={2.5} rowSpacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField label="Your Name" name="name" value={form.name} onChange={onChange} fullWidth required sx={fieldSx} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Email Address" name="email" type="email" value={form.email} onChange={onChange} fullWidth required sx={fieldSx} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Gym Name" name="gymName" value={form.gymName} onChange={onChange} fullWidth sx={fieldSx} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Phone Number" name="phone" value={form.phone} onChange={onChange} fullWidth sx={fieldSx} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Message / Requirements (Optional)"
            name="message"
            value={form.message}
            onChange={onChange}
            fullWidth
            multiline
            rows={2.5}
            placeholder="Tell us about your gym, member count, or specific requirements..."
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sx={{ pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting || !form.name.trim() || !form.email.trim()}
            endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
            sx={{
              ...primaryButton,
              py: 1.6,
              fontSize: "15px",
              fontWeight: 800,
              borderRadius: "14px",
              "&:disabled": {
                background: `linear-gradient(135deg, ${ACCENT} 0%, #6366F1 100%)`,
                opacity: 0.45,
                color: "#FFFFFF",
              },
            }}
          >
            {submitting ? "Sending..." : "Book My Demo"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
